import {mergeAttributes, Node} from "@tiptap/core";
import {ReactNodeViewRenderer} from "@tiptap/react";
import {parseJsonAttribute} from "../shared/blockAttributes";
import PrintoutDataListNodeView from "./PrintoutDataListNodeView";
import {DEFAULT_DATA_LIST_ATTRS} from "./printoutDataListOptions";
import type {
    DataListColumn,
    PrintoutDataListAttrs,
} from "./printoutDataListOptions";

const jsonAttribute = <T,>(dataAttribute: string, fallback: T) => ({
    default: fallback,
    parseHTML: (element: HTMLElement) => (
        parseJsonAttribute<T>(element.getAttribute(dataAttribute), dataAttribute) ?? fallback
    ),
    renderHTML: (attributes: Record<string, unknown>) => ({
        [dataAttribute]: JSON.stringify(attributes[dataAttribute.replace(/^data-/, "")]),
    }),
});

export const PrintoutDataListNode = Node.create({
    name: "printoutDataList",

    group: "block",
    atom: true,
    selectable: true,
    draggable: true,

    addAttributes() {
        return {
            columns: jsonAttribute<DataListColumn[]>("data-columns", DEFAULT_DATA_LIST_ATTRS.columns),
            emptyText: {
                default: DEFAULT_DATA_LIST_ATTRS.emptyText,
                parseHTML: (element: HTMLElement) => element.getAttribute("data-empty-text"),
                renderHTML: (attributes: Record<string, unknown>) => (
                    typeof attributes.emptyText === "string" ? {"data-empty-text": attributes.emptyText} : {}
                ),
            },
            title: {
                default: DEFAULT_DATA_LIST_ATTRS.title,
                parseHTML: (element: HTMLElement) => element.getAttribute("data-title") ?? "",
                renderHTML: (attributes: Record<string, unknown>) => (
                    typeof attributes.title === "string" && attributes.title
                        ? {"data-title": attributes.title}
                        : {}
                ),
            },
            hideWhenEmpty: {
                default: DEFAULT_DATA_LIST_ATTRS.hideWhenEmpty,
                parseHTML: (element: HTMLElement) => element.getAttribute("data-hide-when-empty") === "true",
                renderHTML: (attributes: Record<string, unknown>) => (
                    attributes.hideWhenEmpty === true ? {"data-hide-when-empty": "true"} : {}
                ),
            },
            sectorTypes: jsonAttribute<PrintoutDataListAttrs["sectorTypes"]>(
                "data-sector-types",
                DEFAULT_DATA_LIST_ATTRS.sectorTypes,
            ),
            source: {
                default: DEFAULT_DATA_LIST_ATTRS.source,
                parseHTML: (element: HTMLElement) => element.getAttribute("data-source"),
                renderHTML: (attributes: Record<string, unknown>) => ({"data-source": String(attributes.source)}),
            },
            // Preview-only values are never serialized.
            resolved: {default: null, rendered: false},
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="printout-data-list"]',
            },
        ];
    },

    renderHTML({HTMLAttributes}) {
        return [
            "div",
            mergeAttributes(HTMLAttributes, {
                "data-type": "printout-data-list",
                class: "printout-rendered-data-list",
            }),
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(PrintoutDataListNodeView, {
            trackNodeViewPosition: true,
        });
    },
});
